import React, { useContext, useEffect, useState } from "react";
import { JSDCContext, JSDCProvider } from "jsdc-dui/dist/JSDC/Context";
import JSDC from "jsdc-dui/dist/JSDC";
import { GeoJSON, latLng, latLngBounds, Path } from "leaflet";
import {
  IDuiContextProviderProps,
  DuiContextProvider,
  DuiContext,
} from "jsdc-dui/dist/components/Context";
import DguideWalksApp from "jsdc-dui/dist/components/DguideWalksApp";
import {
  DguidewalksContext,
  DguidewalksProvider,
} from "jsdc-dui/dist/JSDC/Dguidewalks/Context";
import "jsdc-dui/dist/style/index.css";
import "./index.scss";
import ConfigProvider from "jsdc-dui/dist/JSDC/Dguidewalks/ConfigProvider";
import kanban_mobile from "./assets/key-art-m.jpg";
import kanban_popup from "./assets/key-art-popup-kanban.svg";
import kanban from "./assets/kanban.svg";
import MenuItemWithDialog from "jsdc-dui/dist/components/LeftMenuBar/MenuList/MenuItemWithDialog";
import Checkin from "jsdc-dui/dist/components/Icons/Checkin";
import AboutIntroImage from "./assets/intro-photo.svg";

const aboutWalkContent = `臺灣原住民族文化園區位於屏東縣瑪家鄉北葉村，民國76年開園，園區佔地面積約43公頃，設立宗旨在展示及保存臺灣原住民族文化，是國內規模最大的原住民族主題戶外博物館。

為提供民眾較為沉浸式且直覺式的園區體驗，「園區數位地圖」為民眾提供了一個數位形式介面、並橫跨不同地理時空尺度的新形態走讀地圖工具。民眾在瀏覽園區各特色景點及路線介紹內容時，能清楚瞭解整體園區的涵蓋範圍、相關服務設施以及周邊即時氣象狀況，並可透過「導航」功能前往各處特色景點；在使用園區設施及服務的同時，亦能透過「數位集章」功能，留下專屬於個人的園區走讀體驗紀錄。
  
Located in Beiyeh Village, Majia Township, Pingtung County, the Taiwan Indigenous Peoples Cultural Park is the largest indigenous themed outdoor museum in Taiwan. It sits on a land area of around 43 hectares. The park was established in 1987 with the mission of exhibiting and preserving Taiwan indigenous peoples culture.
  
In order to provide visitors a more immersive experience in the park, the "Cultural Park Digital Map" lays out a digital interface and a new form of walking map tool across different geographic, temporal and spatial scales. When browsing through the different park attractions, visitors can get a clear understanding of the park, its facilities and real-time weather broadcast.  In addition, visitors can walk through the park using the "navigation" function to check out various attractions and leave a personal park experience record using the "Collect Digital Stamps" function. `;

const credit = `
<div>
    <ul>
        <li style="list-style-type: cjk-ideographic;">
            本圖臺由原住民族委員會原住民族文化發展中心及智紳數位文化事業有限公司共同協力建置：
            <br><br>1.圖臺内容：原住民族委員會原住民族文化發展中心提供。
            <br><br>2.圖臺系統開發：智紳數位文化事業有限公司。
        </li>
        <br><br>
        <li style="list-style-type: cjk-ideographic;">
            本圖臺内容其内容著作財產權由原住民族委員會原住民族文化發展中心所有，若有其他利用或授權需求請洽原發中心。
        </li>
    </ul>
</div>
電話：08-799 1219 （分機：253）`;

const duiConfigProps: IDuiContextProviderProps = {
  sidebarTitle: "臺灣原住民族文化園區",
  sidebarSubtitle: "",
  aboutWalkImgSrc: AboutIntroImage,
  aboutWalkContent,
  credit,
  creditHref: "https://www.facebook.com/JRSHENDigitalCulture/",
  headerMBImgSrc: kanban_mobile,
  headerDImgSrc: kanban,
  menuSwitchItems: [{ id: "數位集章", name: "數位集章" }],
  weatherConfig: {
    token: "CWB-232A270E-12F1-4381-B9F2-DF2D2670A077",
    locations: [{ county: "屏東縣", town: "瑪家鄉" }],
  },
  themeConfig: {
    "--dui-primary": "#E6A984",
    "--dui-accent": "black",
    "--dui-secondary": "#EB9D1D",
    "--dui-bg-secondary": "#EB9D1D",
    "--dui-bg-accent": "#E6A984",
  },
  settingConfig: {
    disabled: true, // 左側選單不出現「工具設定」
    // languageSwitcherDisabled: true, // 或：保留「工具設定」，只藏語言切換器
  },
};

// 點部落範圍（面）時開啟的景點：面的 `properties.name` → 景點標題
const polygonScenes: Record<string, string> = {
  "瑪卡拉汎區 Makalafang": "41瑪卡拉汎區 Makalafang",
  "卡給尺夫岸區 Kakicevungan": "42卡給尺夫岸區 Kakicevungan",
  "塔瑪麓灣區 Tamaluwan": "43塔瑪麓灣區 Tamaluwan",
  "娜麓灣區 Naluwan": "44娜麓灣區 Naluwan",
  "富谷灣區 Pukubavan": "45富谷灣區 Pukubavan",
};

// 景點的 icon、點擊開集章卡片、集章 key、`?id=` 深連結、列表的定位與導航、
// 底圖、線、面的顏色都由 jsdc-dui 依 Strapi 的資料建立；這裡只留「點面開景點」。
function App() {
  const { Jsdc } = useContext(JSDCContext);
  const dui = useContext(DuiContext);
  const { dgw } = useContext(DguidewalksContext);

  const init = () => {
    Jsdc.Controller.get("Layer")
      .getByName<GeoJSON>("s0004-polygon")
      ?.forEachLayerAsGeoJSON<Path, { name: string }>((layer, properties) => {
        const sceneTitle = polygonScenes[properties.name];
        if (!sceneTitle) return;
        layer.on("click", () => {
          dgw.findSceneByTitle(sceneTitle)?.fireEvent("click");
        });
      });
  };
  useEffect(() => {
    (window as any).JSDC = Jsdc;
    dui.menuSwitch(undefined);
    return dgw.dataLoadEvent.addEventListener(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="root">
      <DguideWalksApp
        language="zh-TW"
        mainMenuChildren={
          <MenuItemWithDialog
            Icon={Checkin}
            title="數位集章"
            active={dui.activeMenuId === "數位集章"}
            {...dui.menuSwitcherAction("數位集章")}
          >
            {dui.activeMenuId === "數位集章" && (
              <iframe
                title="數位集章"
                style={{ height: "100%", border: 0, borderRadius: "5px" }}
                src={
                  "https://map.jsdc.com.tw/tools/checkin/s0004/showlist.php?a=s0004"
                }
              ></iframe>
            )}
          </MenuItemWithDialog>
        }
      />
    </div>
  );
}

const eventId = "s0004";

const config = new ConfigProvider({ eventId });

const AppWrapper: React.FC = () => {
  const [Jsdc] = useState(
    new JSDC(eventId, {
      bound: latLngBounds(
        latLng(22.69979201440863, 120.64775705337526),
        latLng(22.716062989707755, 120.66872119903566),
      ),
      maxZoom: 19,
    }),
  );

  return (
    <JSDCProvider Jsdc={Jsdc}>
      <DguidewalksProvider Jsdc={Jsdc} config={config}>
        <DuiContextProvider
          {...duiConfigProps}
          // 沒有 cluster；集章對話框用另一張圖
          sceneConfig={{ cluster: false, checkinKanbanImgSrc: kanban_popup }}
        >
          <App />
        </DuiContextProvider>
      </DguidewalksProvider>
    </JSDCProvider>
  );
};

export default AppWrapper;
